import React from 'react';
import { motion } from 'framer-motion';
import { ArrowPathIcon } from '@heroicons/react/24/solid';
import { useAppSelector } from 'app/hooks';
import { selectJourneyContext } from '../reducers/onboardingSlice';
import OnboardingLayout from './OnboardingLayout';
import SnowflakeAccountFormV2 from './SnowflakeAccountFormV2';
import SnowflakeSignupCard from './SnowflakeSignupCard';
import SnowflakeIcon from 'components/SnowflakeIcon';
import DataOpsIcon from 'components/DataOpsIcon';

/**
 * SnowflakeSetupStep Component
 *
 * Snowflake account setup step with enhanced design.
 * Combines OnboardingLayout with SnowflakeAccountFormV2 and displays
 * journey-specific messaging when available.
 *
 * Features:
 * - Journey context awareness for personalized messaging
 * - Animated header with Snowflake + DataOps icons
 * - Full integration with Redux state
 * - Support for all three authentication methods
 * - Framer Motion animations
 *
 * @component
 * @example
 * ```tsx
 * <SnowflakeSetupStep />
 * ```
 *
 * @returns Snowflake setup step with enhanced layout and form
 */
const SnowflakeSetupStep: React.FC = () => {
  const journeyContext = useAppSelector(selectJourneyContext);

  return (
    <OnboardingLayout currentStep="snowflake_setup">
      <div className="mx-auto max-w-7xl px-4 pb-12 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="rounded-lg bg-white px-4 py-6 shadow-sm sm:px-6 lg:px-8"
        >
          <div className="space-y-8">
            {/* Header Section */}
            <div className="py-12 flex flex-col sm:flex-row justify-center items-center gap-4 sm:gap-6 border-b border-gray-200">
              <div className="shrink-0">
                <div
                  className="relative h-16 w-16 flex items-center justify-center rounded-full bg-dataops-primary-blue/10"
                  role="img"
                  aria-label="Snowflake and DataOps integration"
                >
                  <SnowflakeIcon
                    size="h-9"
                    className="z-10"
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 0.7, delay: 1, type: 'spring', bounce: 0.7 }}
                  />
                  <DataOpsIcon
                    size="h-7"
                    className="z-10"
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 0.7, delay: 1.2, type: 'spring', bounce: 0.7 }}
                  />
                  <ArrowPathIcon className="absolute top-0 left-0 h-16 text-white" aria-hidden="true" />
                </div>
              </div>
              <div className="text-center sm:text-left">
                <h2 className="text-3xl  text-dataops-dark">Connect your Snowflake Account</h2>
                <p className="mt-1 max-w-2xl text-sm text-secondary-text">
                  {journeyContext != null
                    ? `To use ${journeyContext.app_name} with Snowflake, DataOps.live needs to connect to your Snowflake account.`
                    : 'To use DataOps.live with Snowflake, DataOps.live needs to be able to connect to your Snowflake account.'}
                </p>
              </div>
            </div>

            {/* Snowflake Signup Card */}
            <div className="px-2 sm:px-4 max-w-2xl mx-auto">
              <SnowflakeSignupCard />
            </div>

            {/* Form Section */}
            <div className="px-2 sm:px-4 max-w-2xl mx-auto">
              <SnowflakeAccountFormV2 />
            </div>
          </div>
        </motion.div>
      </div>
    </OnboardingLayout>
  );
};

export default SnowflakeSetupStep;
